import {noop} from '@benchling/js-utils';
import {Accordion, Box, createStyles, Stack, Text} from '@mantine/core';
import React from 'react';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys} from 'procedures/ProceduresStringKeys';
import {TransposedReplicateTable} from 'studies/typed/shared/procedure-runs/conditions/TransposedReplicateTable';
import {summarizeConditionGroups} from 'studies/typed/shared/procedure-runs/conditions/transposedReplicateTableRows';
import {PlanConditionsByMethodId} from 'studies/typed/shared/procedure-runs/design-state/planConditionState';
import {Condition} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {UsePlanStudyDesignStateResult} from 'studies/typed/shared/procedure-runs/design-state/usePlanStudyDesignState';
import MethodExecutionLimitError from 'studies/typed/shared/procedure-runs/execution-status/MethodExecutionLimitError';
import {MethodExecutionLimitState} from 'studies/typed/shared/procedure-runs/execution-status/useMethodExecutionLimitState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';

import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';

export interface UnitOperationsSectionProps {
  planConditionsByMethodId: PlanConditionsByMethodId;
  setConditionsForMethod: UsePlanStudyDesignStateResult['setConditionsForMethod'];
  editState: StudyDesignTabEditState;
  methods: ReadonlyArray<{id: string; name: string}>;
  procedureType: ProcedureType;
  methodExecutionLimitState: MethodExecutionLimitState;
  experimentalInputs: ExperimentalInputs;
}

interface UnitOperationCardProps {
  editState: StudyDesignTabEditState;
  experimentalInputs: ExperimentalInputs;
  method: {id: string; name: string};
  procedureType: ProcedureType;
  rows: Array<Condition>;
  setConditionsForMethod: UnitOperationsSectionProps['setConditionsForMethod'];
}

export default function UnitOperationsSection(props: UnitOperationsSectionProps): JSX.Element {
  const {classes} = useSectionStyles();
  const title = getProcedureUIString(ProceduresStringKeys.METHOD, props.procedureType, {plural: true});
  const isEditing = props.editState === StudyDesignTabEditState.EDITING;

  return (
    <Stack spacing="md" {...getTestProps({component: 'UnitOperationsSection', element: 'root'})}>
      <Text className={classes.title}>{title}</Text>
      <Accordion
        chevronPosition="left"
        classNames={{
          chevron: classes.chevron,
          content: classes.content,
          control: classes.control,
          item: classes.item,
        }}
        multiple={true}
        radius="sm"
        variant="separated"
      >
        {props.methods.map((method) => {
          const rows = props.planConditionsByMethodId[method.id];
          assert(
            rows != null && rows.length > 0,
            `Expected at least one plan condition for method ${method.id}`
          );
          return (
            <UnitOperationAccordionItem
              key={method.id}
              editState={props.editState}
              experimentalInputs={props.experimentalInputs}
              method={method}
              procedureType={props.procedureType}
              rows={rows}
              setConditionsForMethod={props.setConditionsForMethod}
            />
          );
        })}
      </Accordion>
      {isEditing ? (
        <Text className={classes.pasteHelperText}>
          {`Paste from a spreadsheet to add multiple ${getProcedureRunConditionTextForProcedureType(
            props.procedureType,
            {plural: true}
          )}`}
        </Text>
      ) : null}
      {props.methodExecutionLimitState.isOverLimit && (
        <Box mt="md">
          <MethodExecutionLimitError
            replicateLimit={props.methodExecutionLimitState.replicateLimit}
            procedureType={props.procedureType}
          />
        </Box>
      )}
    </Stack>
  );
}

function UnitOperationAccordionItem({
  editState,
  experimentalInputs,
  method,
  procedureType,
  rows,
  setConditionsForMethod,
}: UnitOperationCardProps): JSX.Element {
  const {classes} = useItemStyles();
  const summary = summarizeConditionGroups(rows, procedureType);

  return (
    <Accordion.Item
      value={method.id}
      {...getTestProps({
        component: 'UnitOperationsSection',
        element: 'unitOperation',
        key: method.id,
      })}
      data-amp-safe={false}
    >
      <Accordion.Control>
        <Stack spacing={0}>
          <Text className={classes.methodName}>{method.name}</Text>
          <Text className={classes.summary}>{summary}</Text>
        </Stack>
      </Accordion.Control>
      <Accordion.Panel>
        <UnitOperationConditionsTable
          editState={editState}
          experimentalInputs={experimentalInputs}
          method={method}
          procedureType={procedureType}
          rows={rows}
          setConditionsForMethod={setConditionsForMethod}
        />
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function UnitOperationConditionsTable({
  editState,
  experimentalInputs,
  method,
  procedureType,
  rows,
  setConditionsForMethod,
}: UnitOperationCardProps): JSX.Element {
  const isEditing = editState === StudyDesignTabEditState.EDITING;

  function handleSetConditions(nextRows: Array<Condition>, prevIndexes: Array<number | null>): void {
    setConditionsForMethod(method.id, nextRows, prevIndexes);
  }

  return (
    <TransposedReplicateTable
      cid={`doe-replicates-${method.id}`}
      conditions={rows}
      experimentalInputs={{[method.id]: experimentalInputs[method.id]}}
      hideFooter={true}
      isPlan={true}
      locked={!isEditing}
      procedureType={procedureType}
      setConditions={handleSetConditions}
      setExperimentalInputs={noop}
    />
  );
}

const useSectionStyles = createStyles((theme) => ({
  chevron: {
    color: theme.colors.gray[5],
    marginTop: theme.spacing.md,
  },
  content: {
    padding: 0,
    // The card border is the table's outer edge.
    '& .mediocre-tableEditable-scrollArea': {
      backgroundColor: 'transparent',
      overflowX: 'hidden',
    },
    '& .mediocre-tableEditable-scrollAreaWrapper': {
      borderLeft: 'none',
      borderRight: 'none',
    },
    '& .mediocre-tableEditable-table': {
      // Column widths are an inline pixel sum. min-width fills the card, and fixed layout shares that width.
      minWidth: '100%',
      '& td:first-child': {
        borderLeft: 'none',
      },
      '& td:last-child': {
        borderRight: 'none',
      },
    },
  },
  control: {
    alignItems: 'flex-start',
  },
  item: {
    backgroundColor: theme.white,
    border: `1px solid ${theme.colors.gray[2]}`,
    overflow: 'hidden',
    '&[data-active]': {
      backgroundColor: theme.white,
      borderColor: theme.colors.gray[2],
    },
  },
  pasteHelperText: {
    marginTop: theme.spacing.xs,
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.xs,
    lineHeight: '14px',
  },
  title: {
    color: theme.black,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.other.fontWeights.semibold,
    lineHeight: '20px',
    marginTop: theme.spacing.md,
  },
}));

const useItemStyles = createStyles((theme) => ({
  methodName: {
    color: theme.colors.gray[9],
    fontSize: theme.fontSizes.md,
    fontWeight: theme.other.fontWeights.semibold,
    letterSpacing: 0,
    lineHeight: '16px',
  },
  summary: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.md,
    fontWeight: theme.other.fontWeights.normal,
    letterSpacing: 0,
    lineHeight: '16px',
  },
}));
